import { keyframes, styled } from "goober";
import type { ManagedAiCredit } from "../../hooks/useManagedAiCredit";

const WARN_FROM = 0.7;
const RING_SIZE = 12;
const RING_STROKE = 2;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  trailingZeroDisplay: "stripIfInteger",
});

function creditRatio(credit: ManagedAiCredit): number {
  return Math.min(1, credit.spentUsd / credit.limitUsd);
}

export function creditExhausted(credit: ManagedAiCredit): boolean {
  return creditRatio(credit) >= 1;
}

export function creditWarningColor(credit: ManagedAiCredit): string | null {
  if (creditExhausted(credit)) return "var(--tooltip-fg-subtle)";
  return creditRatio(credit) >= WARN_FROM ? "var(--orange-600)" : null;
}

export function creditLabel(credit: ManagedAiCredit): string {
  if (creditExhausted(credit)) {
    return `${usd.format(credit.limitUsd)} AI credit used up`;
  }
  // Rounds down so unfinished spend never reads as the whole credit.
  const spent = usd.format(Math.floor(credit.spentUsd * 100) / 100);
  return `${spent} of ${usd.format(credit.limitUsd)} AI credit used`;
}

const ringIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.6);
  }
`;

const arcIn = keyframes`
  from {
    stroke-dashoffset: ${RING_CIRCUMFERENCE};
  }
`;

// Springs in and draws its arc when the warning first appears, then eases
// between levels.
const Ring = styled("svg")`
  flex-shrink: 0;
  animation: ${ringIn} 250ms var(--ease-spring);

  & circle + circle {
    transition: stroke-dashoffset 900ms var(--ease-out);
    animation: ${arcIn} 900ms var(--ease-out) 100ms backwards;
  }
`;

export function CreditIndicator({ credit }: { credit: ManagedAiCredit }) {
  const color = creditWarningColor(credit);
  if (!color) return null;
  const center = RING_SIZE / 2;
  return (
    <Ring
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      role="img"
      aria-label={creditLabel(credit)}
    >
      <circle
        cx={center}
        cy={center}
        r={RING_RADIUS}
        fill="none"
        stroke="var(--tooltip-highlight)"
        strokeWidth={RING_STROKE}
      />
      <circle
        cx={center}
        cy={center}
        r={RING_RADIUS}
        fill="none"
        stroke={color}
        strokeWidth={RING_STROKE}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        style={{
          strokeDashoffset: RING_CIRCUMFERENCE * (1 - creditRatio(credit)),
        }}
        transform={`rotate(-90 ${center} ${center})`}
      />
    </Ring>
  );
}
